<script setup lang="ts">
import { computed, ref } from 'vue';
import type { DosageForm, MedicationOrder, SuppliedBy } from '@/api/medications';
import { useCompanyClock } from '@/composables/useCompanyClock';
import { useMobile } from '@/composables/useMobile';
import {
  courseDoses, dosageSummary, humanMoment, nextDose, periodEnd, periodStart, routeLabel, type PeriodChoice,
} from '@/utils/medications';

// Last step: the whole order read back, one row per answer, each with its
// own "Cambiar". The drug, the doses and the period jump back to their step;
// who supplies it and the prescription open in place, since their defaults
// are right most of the time.
const props = defineProps<{
  drugName: string;
  route: string;
  highAlert: boolean;
  doseUnitLabel: string;
  dosages: DosageForm[];
  replacing: MedicationOrder | null;
  // What supplying it means for stock, when the company tracks inventory.
  stockEffect: string;
}>();
const emit = defineEmits<{ editDrug: []; editDosage: []; editPeriod: [] }>();
const period = defineModel<PeriodChoice>('period', { required: true });
const suppliedBy = defineModel<SuppliedBy>('suppliedBy', { required: true });
const prescription = defineModel<{
  prescriberName: string; prescriberIdentifier: string; prescribedOn: string; prescriptionDocument: File | null;
}>('prescription', { required: true });
const reason = defineModel<string>('reason', { default: '' });
const { now } = useCompanyClock();
const { isPhone } = useMobile();

type Editor = 'supplier' | 'prescription';
const editing = ref<Editor | null>(null);
const isChange = computed(() => !!props.replacing);

const start = computed(() => periodStart(period.value));
const end = computed(() => periodEnd(period.value, now()));

const durationText = computed(() => {
  if (!end.value) return 'Sin término';
  const until = humanMoment(end.value, now());
  if (period.value.durationMode === 'until') return `Hasta ${until}`;
  const days = period.value.durationDays === 1 ? '1 día' : `${period.value.durationDays} días`;

  return `Por ${days}, hasta ${until}`;
});

const course = computed(() => {
  if (!end.value) return null;
  const doses = courseDoses(props.dosages, start.value ?? now(), end.value);

  return doses && `${doses.count} dosis · última ${humanMoment(doses.last, now())}`;
});

const firstDose = computed(() => {
  const from = start.value && start.value > now() ? start.value : now();
  const dose = nextDose(props.dosages, from);

  return dose && (!end.value || dose < end.value) ? humanMoment(dose, now()) : null;
});

const startText = computed(() => {
  const when = start.value ? humanMoment(start.value, now()) : 'ahora';

  return firstDose.value ? `${when} · primera dosis ${firstDose.value}` : when;
});

const supplierText = computed(() => {
  if (suppliedBy.value === 'patient') return 'El paciente o su familia';

  return props.stockEffect ? `La empresa · ${props.stockEffect}` : 'La empresa';
});

const prescriptionText = computed(() => {
  const parts = [prescription.value.prescriberName, prescription.value.prescriptionDocument?.name].filter(Boolean);
  if (parts.length > 0) return parts.join(' · ');

  return props.replacing?.prescriptionDocumentUrl ? 'La de la indicación actual' : 'Sin adjuntar';
});

const documentLabel = computed(() => (
  props.replacing?.prescriptionDocumentUrl ? 'Nueva receta (se mantiene la actual)' : 'Receta escaneada'
));

function toggle(editor: Editor) {
  editing.value = editing.value === editor ? null : editor;
}
</script>
<template>
  <div class="d-flex flex-column ga-4">
    <v-sheet
      border
      rounded
    >
      <div
        class="review-row"
        :class="{ phone: isPhone }"
      >
        <div class="review-label">
          Medicamento
        </div>
        <div class="review-value">
          <span class="font-weight-medium">{{ drugName }}</span> · {{ routeLabel(route) }}
          <v-chip
            v-if="highAlert"
            color="error"
            size="x-small"
            variant="flat"
            prepend-icon="mdi-alert"
            class="ml-1"
          >
            Alto riesgo
          </v-chip>
        </div>
        <v-btn
          v-if="!isChange"
          variant="text"
          size="small"
          class="review-action"
          aria-label="Cambiar medicamento"
          @click="emit('editDrug')"
        >
          Cambiar
        </v-btn>
      </div>
      <v-divider />

      <div
        class="review-row"
        :class="{ phone: isPhone }"
      >
        <div class="review-label">
          Dosis
        </div>
        <div class="review-value d-flex flex-column ga-1">
          <div
            v-if="replacing"
            class="text-body-2 text-medium-emphasis"
          >
            Antes: {{ replacing.dosages.map((dosage) => dosage.summary).join(' · ') }}
          </div>
          <div
            v-for="(dosage, index) in dosages"
            :key="index"
          >
            <span class="font-weight-medium">{{ dosageSummary(dosage, doseUnitLabel) }}</span>
            <span
              v-if="dosage.kind === 'as_needed' && dosage.indication"
              class="text-medium-emphasis"
            > · {{ dosage.indication }}</span>
            <span
              v-if="dosage.instructions"
              class="d-block text-body-2 text-medium-emphasis"
            >{{ dosage.instructions }}</span>
          </div>
        </div>
        <v-btn
          variant="text"
          size="small"
          class="review-action"
          aria-label="Cambiar dosis"
          @click="emit('editDosage')"
        >
          Cambiar
        </v-btn>
      </div>
      <v-divider />

      <div
        class="review-row"
        :class="{ phone: isPhone }"
      >
        <div class="review-label">
          Período
        </div>
        <div class="review-value d-flex flex-column ga-1">
          <div>{{ isChange ? 'Rige desde' : 'Desde' }} {{ startText }}</div>
          <div
            v-if="isChange"
            class="text-body-2 text-medium-emphasis"
          >
            La indicación actual sigue vigente hasta entonces.
          </div>
          <div class="text-body-2">
            {{ durationText }}
          </div>
          <div
            v-if="course"
            class="text-body-2 text-medium-emphasis"
          >
            {{ course }}
          </div>
        </div>
        <v-btn
          variant="text"
          size="small"
          class="review-action"
          aria-label="Cambiar el período"
          @click="emit('editPeriod')"
        >
          Cambiar
        </v-btn>
      </div>
      <v-divider />

      <div
        class="review-row"
        :class="{ phone: isPhone }"
      >
        <div class="review-label">
          Lo provee
        </div>
        <div class="review-value">
          {{ supplierText }}
        </div>
        <v-btn
          variant="text"
          size="small"
          class="review-action"
          aria-label="Cambiar quién lo provee"
          @click="toggle('supplier')"
        >
          {{ editing === 'supplier' ? 'Listo' : 'Cambiar' }}
        </v-btn>
      </div>
      <v-expand-transition>
        <div
          v-if="editing === 'supplier'"
          class="review-editor"
        >
          <v-btn-toggle
            v-model="suppliedBy"
            mandatory
            density="compact"
            variant="outlined"
            color="primary"
            divided
          >
            <v-btn
              value="company"
              size="small"
            >
              La empresa
            </v-btn>
            <v-btn
              value="patient"
              size="small"
            >
              El paciente o su familia
            </v-btn>
          </v-btn-toggle>
        </div>
      </v-expand-transition>
      <v-divider />

      <div
        class="review-row"
        :class="{ phone: isPhone }"
      >
        <div class="review-label">
          Receta
        </div>
        <div class="review-value">
          {{ prescriptionText }}
        </div>
        <v-btn
          variant="text"
          size="small"
          class="review-action"
          aria-label="Agregar la receta"
          @click="toggle('prescription')"
        >
          {{ editing === 'prescription' ? 'Listo' : prescriptionText === 'Sin adjuntar' ? 'Agregar' : 'Cambiar' }}
        </v-btn>
      </div>
      <v-expand-transition>
        <div
          v-if="editing === 'prescription'"
          class="review-editor"
        >
          <v-row dense>
            <v-col cols="12">
              <v-file-input
                v-model="prescription.prescriptionDocument"
                :label="documentLabel"
                accept="application/pdf,image/*"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="prescription.prescriberName"
                label="Médico que indica"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="prescription.prescriberIdentifier"
                label="RUT o registro del médico"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="prescription.prescribedOn"
                type="date"
                label="Fecha de la receta"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
    </v-sheet>

    <v-text-field
      v-if="isChange"
      v-model="reason"
      label="Motivo del cambio (opcional)"
      placeholder="Ej.: control médico"
      variant="outlined"
      density="compact"
      hide-details
    />
  </div>
</template>
<style scoped>
.review-row {
  display: flex;
  align-items: flex-start;
  gap: 8px 16px;
  padding: 12px 16px;
}

.review-row.phone {
  flex-wrap: wrap;
}

.review-label {
  flex: 0 0 120px;
  padding-top: 2px;
  font-size: .875rem;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.review-row.phone .review-label {
  flex: 1 1 auto;
  order: 0;
}

.review-value {
  flex: 1 1 auto;
  min-width: 0;
}

.review-row.phone .review-value {
  flex: 1 1 100%;
  order: 2;
}

.review-action {
  flex: 0 0 auto;
  order: 1;
}

.review-editor {
  padding: 0 16px 16px;
}
</style>
